import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../common/types';
import { LoadingView } from '../common/views/LoadingView';
import { HorizontalVerticalCenterContainer } from '../common/views/HorizontalVerticalCenterContainer';
import { SMLContainer } from '../smartLinks/smlContainer/SMLContainer';
import { SMLBreadcrumbs } from '../smartLinks/smlBreadcrumbs/SMLBreadcrumbs';
import { useUserToken } from '../../redux/slices/appInfo/hooks';
import { useSMLRouter } from '../../routing/sml/hooks';
import { SMLRoutes } from '../../routing/sml/SMLRoutes';
import { captureInviteTokenFromLocation } from './teamInviteToken';
import { TeamAcceptFlow } from './TeamAcceptFlow';
import { TeamAcceptIncompleteLink } from './TeamAcceptIncompleteLink';

/**
 * `/team/accept` — the page a team-invite email link opens (`SendTeamInviteEmailJob`). A plain
 * path like `oauth-consent`: reached only from that link, never from `useSMLRouter().push`.
 *
 * The token is read once, from the URL fragment, before anything else runs — a signed-out
 * visitor is about to be sent through sign-in, which reloads the page and drops the fragment.
 */
const TeamAcceptViewComponent: React.FC = () => {
  const intl = useIntl();
  const [token] = useState(() => captureInviteTokenFromLocation());
  const userToken = useUserToken();
  const router = useSMLRouter();
  // The sign-in page shows the generic invite headline (design: onboarding g7). It does not take
  // names from the URL, so the link carries only `returnTo`.
  useEffect(() => {
    if (userToken === null) {
      router.replace(SMLRoutes.auth, { returnTo: 'team/accept' });
    }
  }, [router, userToken]);

  const breadcrumbs = useMemo(
    () => [
      { name: intl.formatMessage({ id: 'app.sml.team.accept.breadcrumb', defaultMessage: 'Join the workspace' }) },
    ],
    [intl]
  );

  const renderContent = () => {
    if (userToken === null) {
      return <LoadingView />;
    }
    if (token === null) {
      return <TeamAcceptIncompleteLink />;
    }
    return <TeamAcceptFlow token={token} />;
  };

  return (
    <SMLContainer header={<SMLBreadcrumbs breadcrumbs={breadcrumbs} />} hidePricingModal>
      <HorizontalVerticalCenterContainer>{renderContent()}</HorizontalVerticalCenterContainer>
    </SMLContainer>
  );
};

export const TeamAcceptView = TypedMemo(TeamAcceptViewComponent);
